import React from 'react';
import type { ElementRef, Ref, ReactNode } from 'react';

import { styled, theme } from '@backyard-ui/styles';
import type { StitchesCSS, CSSProperties } from '@backyard-ui/styles';

import { widthToColumns, countToColumns } from './SimpleGrid.utils';
import { filterFalsyValue } from '../../utils';

import styles from './SimpleGrid.styles';

export interface SimpleGridProps {
  /**
   * The CSS `grid-area` property
   */
  area?: CSSProperties['gridArea'];

  /**
   * The CSS `grid-auto-columns` property
   */
  autoColumns?: CSSProperties['gridAutoColumns'];

  /**
   * The CSS `grid-auto-flow` property
   */
  autoFlow?: CSSProperties['gridAutoFlow'];

  /**
   * The CSS `grid-auto-rows` property
   */
  autoRows?: CSSProperties['gridAutoRows'];

  /**
   * The CSS `gap` property
   */
  gap?: keyof typeof theme.space;

  /**
   * The CSS `grid-column` property
   */
  column?: CSSProperties['gridColumn'];

  /**
   * The CSS `column-gap` property
   */
  columnGap?: keyof typeof theme.space;

  /**
   * The CSS `grid-row` property
   */
  row?: CSSProperties['gridRow'];

  /**
   * The CSS `row-gap` property
   */
  rowGap?: keyof typeof theme.space;

  /**
   * The CSS `grid-template-areas` property
   */
  templateAreas?: CSSProperties['gridTemplateAreas'];

  /**
   * The CSS `grid-template-columns` property
   */
  templateColumns?: CSSProperties['gridTemplateColumns'];

  /**
   * The CSS `grid-template-rows` property
   */
  templateRows?: CSSProperties['gridTemplateRows'];

  /**
   * The number of columns
   */
  columns?: Array<number | null> | number | undefined;

  /**
   * The width at which child elements will break into columns.
   */
  minChildWidth?: string;

  /**
   * The gap between the grid items
   */
  spacing?: keyof typeof theme.space;

  /**
   * The column gap between the grid items
   */
  spacingX?: keyof typeof theme.space;

  /**
   * The row gap between the grid items
   */
  spacingY?: keyof typeof theme.space;

  /**
   * Stitches `css` func
   */
  css?: StitchesCSS;

  /**
   * The ref to the HTML DOM element
   */
  ref?: Ref<HTMLDivElement>;

  /**
   * The children elements
   */
  children: ReactNode;
}

const DEFAULT_TAG = 'div';

const SimpleGridEl = styled(DEFAULT_TAG, styles);

const SimpleGrid = React.forwardRef<
  ElementRef<typeof DEFAULT_TAG>,
  SimpleGridProps
>((props, ref) => {
  const {
    area,
    templateAreas,
    column,
    row,
    autoFlow,
    autoRows,
    templateRows,
    columns,
    autoColumns,
    minChildWidth,
    ...rest
  } = props;

  const grid = minChildWidth
    ? widthToColumns(minChildWidth)
    : countToColumns(columns);

  const styles = filterFalsyValue({
    gridArea: area,
    gridTemplateAreas: templateAreas,
    gridAutoColumns: autoColumns,
    gridColumn: column,
    gridRow: row,
    gridAutoFlow: autoFlow,
    gridAutoRows: autoRows,
    gridTemplateRows: templateRows,
    ...grid,
  });

  return (
    <SimpleGridEl
      css={{
        ...styles,
      }}
      ref={ref}
      {...rest}
    />
  );
});

SimpleGrid.displayName = 'SimpleGrid';

export default SimpleGrid;
